/* eslint-disable react/button-has-type */
/* eslint-disable react/no-unused-class-component-methods */
/* eslint-disable class-methods-use-this */
import React, { Component } from 'react';
import styled from 'styled-components/macro';
import psp from 'assets/images/psp.jpg';

const ErrorModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(5px);
`;

const ErrorContainer = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ErrorHeader = styled.div`
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  padding: 20px;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ErrorIcon = styled.div`
  font-size: 24px;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

const ErrorTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const ErrorContent = styled.div`
  padding: 20px;
`;

const ErrorSection = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px;
  color: #991b1b;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-all;
`;

const StackTrace = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #374151;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.4;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
`;

const InfoItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px;
`;

const InfoLabel = styled.div`
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const InfoValue = styled.div`
  font-size: 13px;
  color: #1e293b;
  font-family: 'Courier New', monospace;
  word-break: break-all;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  &.primary {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;

    &:hover {
      background: #2563eb;
    }
  }

  &.secondary {
    background: white;
    color: #374151;
    border-color: #d1d5db;

    &:hover {
      background: #f9fafb;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 20px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      showModal: false,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    this.setState({
      error,
      errorInfo,
      errorId,
      showModal: true,
    });

    // Store error in localStorage for potential debugging
    try {
      const errorData = {
        errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      };

      const existingErrors = JSON.parse(localStorage.getItem('appErrors') || '[]');
      existingErrors.push(errorData);

      // Keep only last 10 errors
      if (existingErrors.length > 10) {
        existingErrors.splice(0, existingErrors.length - 10);
      }

      localStorage.setItem('appErrors', JSON.stringify(existingErrors));
    } catch (storageError) {
      // Silently fail if localStorage is not available
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleCloseModal = () => {
    this.setState({ showModal: false });
  };

  showCopiedMessage = () => {
    // Create a temporary toast-like notification
    const notification = document.createElement('div');
    notification.textContent = 'Error details copied to clipboard!';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #10b981;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 100000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 14px;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  };

  showFailedMessage = () => {
    const notification = document.createElement('div');
    notification.textContent = 'Failed to copy. Please manually copy the error details.';
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ef4444;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 100000;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      font-size: 14px;
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  };

  handleCopyError = () => {
    const { error, errorInfo, errorId } = this.state;

    const errorText = `
Error ID: ${errorId}
Timestamp: ${new Date().toISOString()}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Viewport: ${window.innerWidth}x${window.innerHeight}

Error Message:
${error?.message || 'Unknown error'}

Stack Trace:
${error?.stack || 'No stack trace available'}

Component Stack:
${errorInfo?.componentStack || 'No component stack available'}
    `.trim();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(errorText)
        .then(() => {
          alert('Error details copied to clipboard!');
        })
        .catch(() => {
          this.fallbackCopyToClipboard(errorText);
        });
    } else {
      this.fallbackCopyToClipboard(errorText);
    }
  };

  fallbackCopyToClipboard = text => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      alert('Error details copied to clipboard!');
    } catch (err) {
      alert('Failed to copy. Please manually copy the error details from the modal.');
    }

    document.body.removeChild(textArea);
  };

  render() {
    const { hasError, error, errorInfo, errorId, showModal } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <>
          {showModal && (
            <ErrorModal onClick={this.handleCloseModal}>
              <ErrorContainer onClick={e => e.stopPropagation()}>
                <CloseButton onClick={this.handleCloseModal}>×</CloseButton>

                <ErrorHeader>
                  <ErrorIcon>💥</ErrorIcon>
                  <ErrorTitle>Application Error Detected</ErrorTitle>
                </ErrorHeader>

                <ErrorContent>
                  <ErrorSection>
                    <SectionTitle>Error Details</SectionTitle>
                    <ErrorMessage>{error?.message || 'An unexpected error occurred'}</ErrorMessage>
                  </ErrorSection>

                  <ErrorSection>
                    <SectionTitle>System Information</SectionTitle>
                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Error ID</InfoLabel>
                        <InfoValue>{errorId}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Timestamp</InfoLabel>
                        <InfoValue>{new Date().toLocaleString()}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Page URL</InfoLabel>
                        <InfoValue>{window.location.href}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Browser</InfoLabel>
                        <InfoValue>{navigator.userAgent.split(' ').slice(-2).join(' ')}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Viewport</InfoLabel>
                        <InfoValue>
                          {window.innerWidth}×{window.innerHeight}
                        </InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Platform</InfoLabel>
                        <InfoValue>{navigator.platform}</InfoValue>
                      </InfoItem>
                    </InfoGrid>
                  </ErrorSection>

                  {error?.stack && (
                    <ErrorSection>
                      <SectionTitle>Stack Trace</SectionTitle>
                      <StackTrace>{error.stack}</StackTrace>
                    </ErrorSection>
                  )}

                  {errorInfo?.componentStack && (
                    <ErrorSection>
                      <SectionTitle>Component Stack</SectionTitle>
                      <StackTrace>{errorInfo.componentStack}</StackTrace>
                    </ErrorSection>
                  )}

                  <ButtonGroup>
                    <Button className="secondary" onClick={this.handleCopyError}>
                      📋 Copy Error Details
                    </Button>
                    <Button className="primary" onClick={this.handleReload}>
                      🔄 Reload Application
                    </Button>
                  </ButtonGroup>
                </ErrorContent>
              </ErrorContainer>
            </ErrorModal>
          )}

          {!showModal && (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                margin: '20px',
              }}>
              <h2 style={{ color: '#991b1b' }}>Something went wrong</h2>
              <p>An error occurred in the application.</p>
              <button onClick={() => this.setState({ showModal: true })}>Show Error Details</button>
              <button onClick={this.handleReload} style={{ marginLeft: '10px' }}>
                Reload Page
              </button>
              <div
                style={{
                  marginTop: '20px',
                  paddingTop: '15px',
                  borderTop: '1px solid #fecaca',
                  color: 'rgba(0,0,0,0.4)',
                  fontSize: '11px',
                  fontWeight: '500',
                }}>
                <img
                  src={psp}
                  alt="PSP Services Inc."
                  style={{ height: 14, marginRight: 6, verticalAlign: 'middle' }}
                />
              </div>
            </div>
          )}
        </>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
